import { useEffect, useState, useCallback } from "react";
import {
    Box,
    Switch,
    Button,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    TextField,
    Skeleton,
    ListItemButton,
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import styles from "./EmailTemplates.module.css";
import { toggleLoader } from "../../store/loader/loaderReducer";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import parse from "html-react-parser";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TiptapControlledEditor from "./TiptapControlledEditor";
import AppHeader from '../../component/appHeader/AppHeader';

const EmailTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState({});
    const [customization, setCustomization] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState("<p>Start writing your email...</p>");
    const [loading, setLoading] = useState(false);
    const [subject, setSubject] = useState("");
    const [open, setOpen] = useState(false);

    const dispatch = useDispatch();
    const { OrgId } = useSelector((state) => state?.document?.value);

    const handleSelectTemplate = useCallback((item) => {
        if (!item?.type) return;
        dispatch(toggleLoader(true));
        axios.get(process.env.REACT_APP_CUSTOM_MAIL_TEMPLATE, {
            withCredentials: process.env.REACT_APP_CREDENTIALS, params: { org_id: OrgId, type: item?.type }
        })
            .then((res) => {
                const templateData = {
                    ...res?.data?.data,
                    type: item?.type,
                    logo_url: res?.data?.logo_url
                };
                setSelectedTemplate(templateData);
                setSubject(res?.data?.data?.subject || "");
            })
            .catch((err) => {
                toast.error(err.message);
                setSelectedTemplate({});
                setSubject("");
            })
            .finally(() => {
                setLoading(false);
                dispatch(toggleLoader(false));
            });
    }, [OrgId, dispatch]);

    const handleGetTemplates = useCallback(() => {
        setLoading(true);
        dispatch(toggleLoader(true));
        axios.get(process.env.REACT_APP_CUSTOM_MAIL_TEMPLATE, {
            withCredentials: process.env.REACT_APP_CREDENTIALS, params: { org_id: OrgId }
        })
            .then((res) => {
                setCustomization(res?.data?.custom_email || false);
                const templatesData = res?.data?.data?.templates || [];
                setTemplates(templatesData);
                if (templatesData.length > 0 && (res?.data?.custom_email || false)) {
                    handleSelectTemplate(templatesData[0]);
                }
            })
            .catch((err) => toast.error(err.message))
            .finally(() => {
                setLoading(false);
                dispatch(toggleLoader(false));
            });
    }, [OrgId, dispatch, handleSelectTemplate]);

    useEffect(() => {
        if (templates?.length) return;
        handleGetTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (customization && templates?.length > 0 && !selectedTemplate?.type) {
            handleSelectTemplate(templates[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customization, templates]);

    const handleUpdate = () => {
        dispatch(toggleLoader(true));
        const data = { org_id: OrgId, type: selectedTemplate?.type, subject: subject, content: content, is_custom: customization };
        axios.post(process.env.REACT_APP_CUSTOM_MAIL_TEMPLATE, data, {
            withCredentials: process.env.REACT_APP_CREDENTIALS
        })
            .then((res) => {
                handleSelectTemplate(selectedTemplate);
                setIsEditing(false);
            })
            .catch((err) => toast.error(err.message))
            .finally(() => {
                setLoading(false);
                dispatch(toggleLoader(false));
            });
    };

    const cleanHTML = (htmlString) => {
        if (!htmlString) return "";

        let cleaned = htmlString;

        cleaned = cleaned.replace(/<!DOCTYPE[^>]*>/gi, "");
        cleaned = cleaned.replace(/<html[^>]*>/gi, "");
        cleaned = cleaned.replace(/<\/html>/gi, "");
        cleaned = cleaned.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "");
        cleaned = cleaned.replace(/<body[^>]*>/gi, "");
        cleaned = cleaned.replace(/<\/body>/gi, "");

        cleaned = cleaned.replace(/cd valign="top" align="cm."/gi, '');
        cleaned = cleaned.replace(/style="font-size: 6px; line-height: 100%; padding: 90% SQPx 15 px";/gi, '');
        cleaned = cleaned.replace(/en href="{{ERROR_MESSILE}}">/gi, '<a href="{{ERROR_MESSAGE}}">');
        cleaned = cleaned.replace(/target="blank">/gi, 'target="_blank">');
        cleaned = cleaned.replace(/clip border="up" width="180"/gi, '');
        cleaned = cleaned.replace(/alt="{{ERROR_IMAGE}}" src="{{ERROR_LOCUM}}" style="display_lock;/gi,
            'alt="{{ERROR_IMAGE}}" src="{{ERROR_LOCUM}}" style="display: block;');

        cleaned = cleaned.replace(/color: rgb0, o, 0\); text-document: none;/gi,
            'color: rgb(0, 0, 0); text-decoration: none;');

        cleaned = cleaned.replace(/ont-family: helvetica, arla 1, sans-serif; font-size: 16 px; width: 380%; = 30/gi,
            'font-family: helvetica, arial, sans-serif; font-size: 16px; width: 380px; height: 30px;');
        cleaned = cleaned.replace(/x\/10>\s*y\/10>\s*x\/1000>\s*y\/table>/gi, '');
        cleaned = cleaned.replace(/visible role="module" border="0"/gi, '');
        cleaned = cleaned.replace(/colpadding="0" collepsing="0"/gi, 'cellpadding="0" cellspacing="0"');
        cleaned = cleaned.replace(/click down: only "style="font-size">/gi, '');

        return cleaned;
    };

    const replaceTemplateVariables = (html, variables = {}) => {
        let processedHtml = html;

        const defaultVars = {
            ORG_LOGO: selectedTemplate.logo_url || '/default-logo.png',
            ERROR_IMAGE: 'Organization Logo',
            ERROR_MESSAGE: '#',
            ORG_NAME: 'Centilio Sign',
            USERNAME: 'Recipient Name',
            ADMIN: 'Admin Name',
            DIGESTLINK: '#'
        };

        const allVars = { ...defaultVars, ...variables };

        Object.entries(allVars).forEach(([key, value]) => {
            processedHtml = processedHtml.replace(new RegExp(`{{${key}}}`, 'g'), value);
            processedHtml = processedHtml.replace(new RegExp(`\\(if@${key}\\)`, 'g'), value);
            processedHtml = processedHtml.replace(new RegExp(`{{#@${key}}}`, 'g'), value);
        });

        return processedHtml;
    };

    const handleReset = (type) => {
        dispatch(toggleLoader(true));
        const data = { org_id: OrgId, type: selectedTemplate?.type };
        if (type === "enable") delete data.type;
        axios.delete(process.env.REACT_APP_CUSTOM_MAIL_TEMPLATE, {
            withCredentials: process.env.REACT_APP_CREDENTIALS, data: data
        })
            .then((res) => {
                if (type === "enable") {
                    setCustomization(res?.data?.response?.custom_email);
                    return;
                }
                setIsEditing(false);
                setOpen(false);
                handleGetTemplates();
            })
            .catch((err) => toast.error(err.message))
            .finally(() => {
                setLoading(false);
                dispatch(toggleLoader(false));
            });
    };

    const handleClose = () => setOpen(false);

    const handleResetTemplate = () => {
        handleReset("enable");
    };

    const handleChangeCustomization = () => {
        dispatch(toggleLoader(true));
        const newCustomizationValue = !customization;
        const data = { org_id: OrgId, is_custom: newCustomizationValue };
        axios.post(process.env.REACT_APP_CUSTOM_MAIL_TEMPLATE, data, {
            withCredentials: process.env.REACT_APP_CREDENTIALS
        })
            .then((res) => {
                setCustomization(newCustomizationValue);
                if (newCustomizationValue && templates?.length > 0 && !selectedTemplate?.type) {
                    handleSelectTemplate(templates[0]);
                }
            })
            .catch((err) => toast.error(err.message))
            .finally(() => {
                setLoading(false);
                dispatch(toggleLoader(false));
            });
    };

    return (
        <Box sx={{ padding: '0px 30px 30px', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            <AppHeader
                moduleText='Email templates'
                rolePermissions={true}
                checkAdmin={true}
            />

            {!loading ?
                <>
                    {!isEditing ? (
                        <Box className={styles.container}>
                            <Box className={styles.sidebar} sx={{ width: customization ? "320px" : "100%", flexShrink: 0 }}>
                                <Box className={styles.header}>
                                    <Box className={styles.switchRow}>
                                        <Typography fontFamily="var(--primary-font-family)" sx={{ color: "var(--table-font-color)", fontSize: "14px", fontWeight: 500 }}>
                                            Enable customization
                                        </Typography>
                                        <Switch
                                            checked={customization}
                                            onChange={handleChangeCustomization}
                                        />
                                    </Box>
                                    <Button variant="text" className={styles.resetBtn} onClick={handleResetTemplate}>
                                        Reset all templates
                                    </Button>
                                </Box>
                                {customization ? <>
                                    <Typography variant="subtitle1" className={styles.templateTitle}>
                                        Choose a template
                                    </Typography>
                                    <List className={styles.templateList}>
                                        {templates?.map((tpl) => (
                                            <ListItem key={tpl?.type} disablePadding>
                                                <ListItemButton
                                                    selected={tpl?.type === selectedTemplate?.type}
                                                    onClick={() => handleSelectTemplate(tpl)}
                                                    className={styles.templateItem}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        "&:hover .chevron-icon": {
                                                            opacity: 1,
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={tpl?.name}
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                fontFamily: "var(--primary-font-family)",
                                                                fontSize: 14,
                                                                fontWeight: tpl?.type === selectedTemplate?.type ? 600 : 500,
                                                            },
                                                        }}
                                                    />
                                                    <ChevronRightIcon
                                                        className="chevron-icon"
                                                        sx={{
                                                            fontSize: 18,
                                                            opacity: 0,
                                                            transition: 'opacity 0.2s',
                                                            color: 'var(--table-font-color)'
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </> :
                                    (
                                        <Box sx={{ padding: "24px 20px" }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    mb: 2,
                                                    fontFamily: "var(--primary-font-family)",
                                                    fontSize: "14px",
                                                    lineHeight: 1.6,
                                                    color: "var(--table-font-color)",
                                                }}
                                            >
                                                The Email Templates section allows you to customize the emails sent
                                                from your Centilio Sign account to match your organization's needs.
                                            </Typography>

                                            <Typography
                                                component="div"
                                                variant="body2"
                                                sx={{
                                                    fontFamily: "var(--primary-font-family)",
                                                    fontSize: "14px",
                                                    lineHeight: 1.8,
                                                    color: "var(--table-font-color)",
                                                }}
                                            >
                                                <ol style={{ paddingLeft: "18px", margin: 0 }}>
                                                    <li style={{ marginBottom: "8px" }}>Enable email customization</li>
                                                    <li style={{ marginBottom: "8px" }}>Choose a template from the list</li>
                                                    <li style={{ marginBottom: "8px" }}>
                                                        Click Edit template and make your changes
                                                    </li>
                                                    <li>Save the template</li>
                                                </ol>
                                            </Typography>
                                        </Box>
                                    )}
                            </Box>

                            {customization && (
                                <Box className={styles.preview}>
                                    <Box sx={{ width: "100%", px: 5 }}>
                                        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", mb: 4, flexWrap: "nowrap" }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontFamily: "var(--primary-font-family)", color: "#6b7280", fontSize: "14px", fontWeight: 500, mb: 1 }}>
                                                    Email subject
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={subject}
                                                    className={styles.subjectBox}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "12px",
                                                            backgroundColor: "white",
                                                            "& fieldset": {
                                                                borderColor: "var(--border-color)",
                                                            },
                                                            "&:hover fieldset": {
                                                                borderColor: "var(--primary)",
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                borderColor: "var(--primary)",
                                                                borderWidth: "2px",
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        readOnly: true,
                                                        sx: { fontSize: 14, cursor: "default", padding: "10px 14px" },
                                                    }}
                                                />
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setIsEditing(true);
                                                    setContent(selectedTemplate?.mail_template || "<p>Start writing your email...</p>");
                                                }}
                                                className={styles.editBtn}
                                                disabled={!selectedTemplate?.type}
                                                startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                                            >
                                                Edit template
                                            </Button>
                                        </Box>

                                        <Box className={styles.emailPreview}>
                                            {selectedTemplate?.mail_template ? (
                                                parse(
                                                    replaceTemplateVariables(
                                                        cleanHTML(selectedTemplate.mail_template.toString()),
                                                        {
                                                            ERROR_LOCUM: selectedTemplate.logo_url,
                                                            ORG_NAME: "Your Organization Name",
                                                            USERNAME: "Recipient Name",
                                                            ADMIN: "Admin Name",
                                                            DIGESTLINK: "#"
                                                        }
                                                    )
                                                )
                                            ) : (
                                                <Typography sx={{ textAlign: "center", color: "var(--table-font-color)", fontFamily: "var(--primary-font-family)", padding: "60px 20px" }}>
                                                    {templates?.length > 0 ? "Select a template to preview" : "No templates available"}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box className={styles.editorContainer}>

                            <Box className={styles.editorCard}>
                                <Box className={styles.editorCardHeader}>
                                    <Typography className={styles.editorCardTitle}>
                                        Edit Email Template
                                    </Typography>
                                </Box>

                                <Box className={styles.editorCardContent}>
                                    <Box className={styles.subjectSection}>
                                        <Typography className={styles.subjectLabel}>
                                            Email subject
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                            placeholder="Enter email subject"
                                            className={styles.subjectInput}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f8fafc",
                                                    fontSize: "14px",
                                                    fontFamily: "var(--primary-font-family)",
                                                    "& fieldset": {
                                                        borderColor: "var(--border-color)",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "var(--primary)",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "var(--primary)",
                                                        borderWidth: "2px",
                                                    },
                                                },
                                                "& .MuiOutlinedInput-input": {
                                                    padding: "10px 16px",
                                                },
                                            }}
                                        />
                                    </Box>

                                    <Box className={styles.editorWrapper}>
                                        <TiptapControlledEditor
                                            initialContent={content || "<p>Start writing...</p>"}
                                            onChange={(value) => setContent(value)}
                                        />
                                    </Box>
                                </Box>

                                <Box className={styles.editorCardFooter}>
                                    <Button
                                        variant="contained"
                                        onClick={handleUpdate}
                                        className={styles.saveButton}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => setOpen(true)}
                                        className={styles.resetButton}
                                    >
                                        Reset To Default
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setIsEditing(false)}
                                        className={styles.cancelButton}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                    )}
                </> : (
                    <Skeleton
                        sx={{ bgcolor: "rgba(189,197,209,.2)", margin: "10px" }}
                        animation="wave"
                        variant="rounded"
                        width="calc(100% - 20px)"
                        height="calc(100vh - 80px)"
                    />
                )}
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="reset-template-dialog"
                PaperProps={{
                    sx: {
                        width: "550px",
                        maxWidth: "90%",
                        borderRadius: "12px"
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: "bold", fontFamily: "var(--primary-font-family)", color: "var(--header-color)" }}>
                    Reset template
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500]
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Resetting the template will also update the base HTML styles and tags.
                        Do you want to continue?
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 4 }}>
                    <Button onClick={handleClose} sx={{ textTransform: "capitalize" }} variant="outlined">
                        No
                    </Button>
                    <Button onClick={handleReset} sx={{ textTransform: "capitalize" }} variant="contained">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmailTemplates;